import { ApolloError, gql } from '@apollo/client';
import { useQuery } from 'platform/useQuery';
import { centsToCurrencyString } from '@opendoor/shared-fe/build/formatUtils';

import {
  CalculateLateCheckoutCosts,
  CalculateLateCheckoutCostsVariables,
} from 'sellDirect/graphql/__generated__/CalculateLateCheckoutCosts';
import {
  UnderContractOffer,
  ClosedOffer,
  WithdrawnOffer,
  FinalizedOffer,
} from 'sellDirect/OfferContext';
import { ClosingTasks } from 'sellDirect/helpers/closingTasks';
import { ChronoUnit, LocalDate } from '@js-joda/core';

export const NO_LATE_CHECKOUT_COST_TEXT = '-';

export const GetLateCheckoutCostsQuery = gql`
  query CalculateLateCheckoutCosts($webOfferToken: String!, $numberOfDays: Int!) {
    seller {
      lateCheckoutCosts(webOfferToken: $webOfferToken, numberOfDays: $numberOfDays) {
        costPerDayCents
        fixedCostCents
        totalCostCents
        freeDaysLateCheckout
      }
    }
  }
`;

export interface ILateCheckoutTextData {
  costPerDayDollarsText: string; // how much late checkout costs per one day (differs per market)
  fixedCostDollarsText: string; // The base cost for late checkout (differs per market)
  numberOfDays: number; // how many days of Close of escrow date that they want to late checkout for
  totalCostDollarsText: string; // total cost for late checkout to the customer (ie subtract out any "free" promo days)
  totalCostDollarsAllDaysText: string; // total cost for late checkout to the customer before any "free" promo days are applied
  freeDaysCreditText: string; // the amount the customer saves on "free" promo late checkout, based on the number of free days
  lateCheckoutConfirmed: boolean; // if a latecheckout has been saved to the late-checkout taskable
  freeDaysLateCheckout: number; // number of days the "free" promo late checkout is applied
  isDeciding: boolean; // if the user is editing the latecheckout date, this will be True
}

interface IUseLateCheckoutReturn {
  loading?: boolean;
  error?: ApolloError | undefined;
  data: ILateCheckoutTextData;
}

export const defaultLateCheckoutData = {
  fixedCostDollarsText: '',
  costPerDayDollarsText: '',
  totalCostDollarsText: NO_LATE_CHECKOUT_COST_TEXT,
  totalCostDollarsAllDaysText: NO_LATE_CHECKOUT_COST_TEXT,
  freeDaysCreditText: '',
  numberOfDays: 0,
  isDeciding: false,
  lateCheckoutConfirmed: false,
  freeDaysLateCheckout: 0,
};

/**
 * @description
 * useLateCheckout hook - queries for late-checkout information
 *
 * @param {UnderContractOffer} offer - the offer
 * @param {ClosingTasks['lateCheckout']} lateCheckoutTask - the late-checkout-task
 * @param {string} lateCheckoutDateOverride - the override late checkout date
 *
 */
export const useLateCheckout = (
  offer: FinalizedOffer | UnderContractOffer | WithdrawnOffer | ClosedOffer,
  lateCheckoutTask: ClosingTasks['lateCheckout'],
  lateCheckoutDateOverride?: LocalDate,
): IUseLateCheckoutReturn => {
  ////////////////////////////////
  // Number of Days Calculation //
  ////////////////////////////////
  const lateCheckoutTaskable = lateCheckoutTask?.taskable;
  const lateCheckoutDate =
    lateCheckoutDateOverride || lateCheckoutTaskable?.lateCheckoutDate || null;

  // If there is no latecheckout date, there is no need to get the late-checkout costs.
  // Additionally, if there is no closeEscrowDate, the customer hasn't
  //  signed a contract yet (and thus hasn't set up their COE date), so there is no need to
  //  get the late-checkout costs
  const numberOfDays =
    lateCheckoutDate && offer.closeEscrowDate
      ? offer.closeEscrowDate.until(lateCheckoutDate, ChronoUnit.DAYS)
      : 0;

  //////////////
  // useQuery //
  //////////////
  const { error, loading, data } = useQuery<
    CalculateLateCheckoutCosts,
    CalculateLateCheckoutCostsVariables
  >(GetLateCheckoutCostsQuery, {
    skip: !offer.leadToken || !offer.closeEscrowDate,
    variables: {
      webOfferToken: offer.leadToken ?? '',
      numberOfDays,
    },
    errorPolicy: 'all',
  });

  if (error || loading) {
    return {
      error,
      loading,
      data: defaultLateCheckoutData,
    };
  }

  ///////////////////////
  // Get Number Fields //
  ///////////////////////
  const costPerDayCents = data?.seller?.lateCheckoutCosts?.costPerDayCents || 0;
  const fixedCostCents = data?.seller?.lateCheckoutCosts?.fixedCostCents || 0;
  const totalCostCents = data?.seller?.lateCheckoutCosts?.totalCostCents || 0;
  const freeDaysLateCheckout = data?.seller?.lateCheckoutCosts?.freeDaysLateCheckout || 0;
  const calculatedTotalCostAllDaysCents = numberOfDays
    ? fixedCostCents + numberOfDays * costPerDayCents
    : 0;

  ///////////////////
  // Get Text Info //
  ///////////////////
  const fixedCostDollarsText = fixedCostCents ? `${centsToCurrencyString(fixedCostCents)} + ` : '';
  const costPerDayDollarsText = costPerDayCents ? centsToCurrencyString(costPerDayCents) : '';
  const totalCostDollarsText = totalCostCents
    ? centsToCurrencyString(totalCostCents)
    : NO_LATE_CHECKOUT_COST_TEXT;
  const totalCostDollarsAllDaysText = calculatedTotalCostAllDaysCents
    ? centsToCurrencyString(calculatedTotalCostAllDaysCents)
    : NO_LATE_CHECKOUT_COST_TEXT;
  const freeDaysCreditText =
    freeDaysLateCheckout && costPerDayCents
      ? centsToCurrencyString(freeDaysLateCheckout * costPerDayCents)
      : '';

  ////////////////////////
  // Miscellaneous Info //
  ////////////////////////
  const lateCheckoutConfirmed = !!lateCheckoutTaskable?.lateCheckoutDate;
  // NOTE: If there was a lateCheckoutDateOverride sent into this component, then the user is manually
  // selecting a date through a calendar picker in the UI. Therefore, the user "isDeciding" is true.
  const isDeciding = !!lateCheckoutDateOverride;

  return {
    loading: false,
    data: {
      fixedCostDollarsText,
      costPerDayDollarsText,
      totalCostDollarsText,
      totalCostDollarsAllDaysText,
      freeDaysCreditText,
      numberOfDays,
      isDeciding,
      lateCheckoutConfirmed,
      freeDaysLateCheckout,
    },
  };
};
